import { type QuoteDTO } from "~/features/orders/quoteList/api/fetchQuoteHistory/dto";
import * as ApiHelper from "~/common/utils/ApiHelper";
import { apiPOST } from "~/app/api/apiCallsService";
import { constructUrl, resolveApiUrl, resolveUrl } from "~/utils/url/urlResolver";
import { type QuoteData } from "~/domains/orders/types/modelsV2";
import {
  constructOrderDetailsUrl,
  isFromDifferentAccOrSalesOrg,
  transformOrderAccount,
} from "~/features/orders/orderList/api/fetchOrderHistory/fetchOrderHistory";
import { type PaginatedFetchFuncResponse } from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { type GroupedAccountsData } from "~/features/orders/common/DownloadOrderHistoryService/types";
import { type QuoteDetailsDTO } from "~/features/orders/quoteList/api/fetchOrderDetails/dto";

import { zero } from "~/utils/PriceUtils";

import { transformQuoteDetailLineItem } from "~/features/orders/quoteList/api/fetchOrderDetails/transformQuoteDetails";

export interface FetchQuoteHistoryProps {
  data: FetchQuoteHistoryRequestData;
  includeDetails?: boolean;
  skipToken?: string;
  signal?: AbortSignal;
}

export interface FetchQuoteHistoryRequestData {
  customerOrderId?: string;
  salesDocument?: string;
  lineCustomerOrderId?: string;
  partNumber?: string;
  matnr?: string;
  customerMatnr?: string;
  sdCreationStart?: string;
  sdCreatenEnd?: string;
  sdStatus: string;
  sdLineStatuses: string[];
  account?: GroupedAccountsData;
}

interface OrderHistoryResponseDTO {
  ocsalesDocumentDataList: QuoteDTO[];
  nextSkipToken: string | undefined;
  pageCount: number;
  totalCount: number;
}

interface ResponseData {
  results: QuoteData[];
  totalResults: number;
  pageCount: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FetchQuoteHistoryResponse extends PaginatedFetchFuncResponse<ResponseData> {}

export async function fetchQuoteHistory({
  data,
  skipToken,
  signal,
}: FetchQuoteHistoryProps): Promise<FetchQuoteHistoryResponse> {
  let url = resolveApiUrl(`/my-account/quotes/searchQuotes`);
  if (skipToken !== undefined) {
    const params = new URLSearchParams();
    params.set("skiptoken", skipToken);
    url = constructUrl(url, params);
  }

  const response = await apiPOST(url, {
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json",
      CSRFToken: ApiHelper.getCsrfValue(),
    },
    signal,
  });

  const dto: OrderHistoryResponseDTO = await response.json();

  return {
    data: {
      results: dto.ocsalesDocumentDataList.map(transformQuote),
      totalResults: dto.totalCount,
      pageCount: dto.pageCount,
    },
    nextSkipToken: dto.nextSkipToken,
  };
}

export function constructQuoteDetailsUrl(soNumber: string | null | undefined, dto: QuoteDTO | QuoteDetailsDTO) {
  let url = "";
  if (soNumber) {
    url = resolveUrl(`/my-account/quotes/detail?id=${soNumber}`);
    if (isFromDifferentAccOrSalesOrg(dto)) {
      const params = new URLSearchParams();
      params.append("account", dto.account!.code);
      params.append("salesOrg", dto.account!.salesOrg);
      url = url + "&" + params.toString();
    }
  }
  return url;
}

export function transformQuote(dto: QuoteDTO): QuoteData {
  const orderData = transformQuoteProducts(dto);

  return {
    ...orderData,

    detailsUrl: constructQuoteDetailsUrl(dto.salesDocument, dto),
    orderAttainment: dto.quoteResponsibleName ?? "",
    isBundled: dto.isBundled ?? false,
    quoteNumber: dto.quoteNumber || dto.salesDocument || "", // treated as special, ONLY for Quotes, if the key "quoteNumber" is missing, then fallback to "salesDocument" else, empty
    quoteDocumentUrl: dto.quoteDocumentUrl ?? "",
  };
}

function transformQuoteProducts(dto: QuoteDTO): QuoteData {
  const products = dto.products ?? [];

  return {
    detailsUrl: constructOrderDetailsUrl(dto.salesDocument, dto),
    datePlaced: dto.datePlaced ? new Date(dto.datePlaced) : undefined,
    orderStatus: dto.sdStatus || "",
    orderStatusCode: dto.sdStatusCode || "",
    orderType: dto.orderType || "",
    soNumber: dto.salesDocument || "",
    poNumber: dto.customerOrderId || "",
    totalPrice: dto.price ? dto.price : zero(),
    products: products ? products.map(transformQuoteDetailLineItem) : [],
    hasSurcharge: dto.hasSurcharge || false,
    account: transformOrderAccount(dto.account),
    isBundled: false,
    orderAttainment: "",
    quoteNumber: dto.quoteNumber || "",
  };
}
